import "./SkeletonCards.css"
import { useRef, useState } from "react"
import { ROOMS_LENGTH_SKELETON, ROOMS_POSITIONS_SKELETON, ROOMS_GRID_SKELETON } from "src/shared/constants/localStorage"
import { useSetSkeletonCardSize } from "./hooks/useSetSkeletonCardSize"
import { RoomCardSizes, CARD_XL_SIZE } from "../RoomCard/RoomCard"
import SkeletonCardXS from "./SkeletonCard/SkeletonCardXS"
import SkeletonCardSM from "./SkeletonCard/SkeletonCardSM"
import SkeletonCardMD from "./SkeletonCard/SkeletonCardMD"
import SkeletonCardLG from "./SkeletonCard/SkeletonCardLG"
import SkeletonCardXL from "./SkeletonCard/SkeletonCardXL"
import SkeletonCardMXL from "./SkeletonCard/SkeletonCardMXL"
import { ComponentType } from "react"

type SavedPosition = { x: number; y: number }
type SavedGrid = { rows: number; cols: number }

const FALLBACK_SIZES: Record<RoomCardSizes, { width: number; height: number }> = {
    xs:  { width: 106, height: 70  },
    sm:  { width: 129, height: 92  },
    md:  { width: 161, height: 118 },
    lg:  { width: 263, height: 103 },
    xl:  { width: CARD_XL_SIZE.x, height: CARD_XL_SIZE.y },
    mxl: { width: 264, height: 242 },
}

const CARD_COMPONENTS: Record<RoomCardSizes, ComponentType> = {
    xs:  SkeletonCardXS,
    sm:  SkeletonCardSM,
    md:  SkeletonCardMD,
    lg:  SkeletonCardLG,
    xl:  SkeletonCardXL,
    mxl: SkeletonCardMXL,
}

function SkeletonCards(): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null)
    const [roomsLength] = useState(Number(localStorage.getItem(ROOMS_LENGTH_SKELETON) || 30))
    const [positions] = useState<SavedPosition[]>(() => {
        try { return JSON.parse(localStorage.getItem(ROOMS_POSITIONS_SKELETON) || "[]") }
        catch { return [] }
    })
    const [grid] = useState<SavedGrid | null>(() => {
        try { return JSON.parse(localStorage.getItem(ROOMS_GRID_SKELETON) || "null") }
        catch { return null }
    })

    const { size } = useSetSkeletonCardSize(grid)
    const fallback = FALLBACK_SIZES[size] ?? FALLBACK_SIZES.xl
    const CardComponent = CARD_COMPONENTS[size] ?? SkeletonCardXL
    const hasLayout = positions.length > 0 && grid !== null

    if (hasLayout) {
        return (
            <div className="room-cards__wrapper__wrapper scrollbar__light-fat" ref={containerRef}>
                <div
                    className="room-cards__wrapper"
                    style={{
                        gridTemplate:
                            size === "xl"
                                ? `repeat(${grid!.rows}, minmax(${CARD_XL_SIZE.y}px, 1fr)) / repeat(${grid!.cols}, minmax(${CARD_XL_SIZE.x}px, 1fr))`
                                : `repeat(${grid!.rows}, 1fr) / repeat(${grid!.cols}, 1fr)`,
                    }}
                >
                    {positions.map((pos, i) => (
                        <div
                            key={i}
                            style={{
                                gridArea: `${pos.y + 1} / ${pos.x + 1} / span 1 / span 1`,
                                ...(size === "xl"
                                    ? { width: CARD_XL_SIZE.x, height: CARD_XL_SIZE.y, minWidth: CARD_XL_SIZE.x, minHeight: CARD_XL_SIZE.y }
                                    : {}),
                            }}
                        >
                            <CardComponent />
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className="home-screen-skeleton-container" ref={containerRef}>
            <div className="room-skeleton-cards">
                {Array.from({ length: roomsLength }, (_, i) => (
                    <div key={i} style={{ width: fallback.width, height: fallback.height }}>
                        <CardComponent />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default SkeletonCards
